import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Grid, Compass } from 'lucide-react';
import { getWebsiteCategories, type WebsiteCategory } from '@/services/api/website-category.service';
import { buildPath, ROUTES } from '@/config/routes';

interface CategorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CategorySidebar({ isOpen, onClose }: CategorySidebarProps) {
  const [categories, setCategories] = useState<WebsiteCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getWebsiteCategories()
      .then((data) => {
        if (mounted) setCategories(data);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Prevent scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-55 bg-black/80 backdrop-blur-md transition-opacity duration-500 ease-out ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={`fixed bottom-0 left-0 top-0 z-55 flex h-full w-80 sm:w-96 max-w-full flex-col bg-black text-white border-r border-zinc-900 transition-all duration-500 cubic-bezier(0.16, 1, 0.3, 1) ${
          isOpen ? 'translate-x-0 shadow-[20px_0_60px_rgba(0,0,0,0.4)]' : '-translate-x-full shadow-none'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-900 px-6 py-6">
          <h2 className="flex items-center gap-3 text-sm font-black uppercase tracking-[0.2em] text-white">
            <Compass className="h-5 w-5 text-zinc-400" />
            Navigation
          </h2>
          <button
            onClick={onClose}
            className="group flex h-10 w-10 items-center justify-center border border-zinc-800 bg-transparent text-zinc-400 transition-all duration-300 hover:border-white hover:text-white rounded-none"
            aria-label="Close menu"
          >
            <X className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />
          </button>
        </div>

        {/* Categories List */}
        <nav className="flex-1 overflow-y-auto px-6 py-8">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 w-full animate-pulse bg-zinc-900" />
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="text-sm text-zinc-500">No categories found.</div>
          ) : (
            <ul className="space-y-6">
              {/* All Products */}
              <li
                className={`transition-all duration-500 transform ${
                  isOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
                }`}
                style={{ transitionDelay: '50ms' }}
              >
                <Link
                  to={ROUTES.SHOP}
                  onClick={onClose}
                  className="group flex items-center justify-between border-b border-zinc-900 pb-4 font-black uppercase tracking-widest text-white transition-all duration-300 text-lg hover:text-zinc-400"
                >
                  <span className="flex items-center gap-3">
                    <Grid className="h-5 w-5 text-zinc-400" />
                    Shop All
                  </span>
                  <ArrowRight className="h-5 w-5 transform transition-transform duration-300 -rotate-45 group-hover:rotate-0 group-hover:translate-x-1" />
                </Link>
              </li>

              {/* Dynamic Categories */}
              {categories.map((category, index) => (
                <li
                  key={category.uuid}
                  className={`transition-all duration-500 transform ${
                    isOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
                  }`}
                  style={{ transitionDelay: `${(index + 2) * 50}ms` }}
                >
                  <Link
                    to={buildPath.category(category.slug)}
                    onClick={onClose}
                    className="group flex items-center justify-between border-b border-zinc-900 pb-4 font-bold text-zinc-300 transition-all duration-300 hover:text-white"
                  >
                    <span className="flex items-center gap-4">
                      {category.image && (
                        <div className="relative h-12 w-12 overflow-hidden border border-zinc-800 bg-zinc-950 shrink-0">
                          <img
                            src={category.image}
                            alt={category.name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </div>
                      )}
                      <span className="text-sm font-black uppercase tracking-[0.15em] transition-transform duration-300 group-hover:translate-x-1">
                        {category.name}
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-zinc-500 transform transition-all duration-300 -rotate-45 group-hover:rotate-0 group-hover:text-white group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </nav>
      </aside>
    </>
  );
}
